/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Twilio Device lifecycle. The only file in the app that imports the SDK.
 *
 * Everything provider-specific is contained here so the rest of the call
 * center talks in terms of "a call" rather than "a Twilio Call", and so a
 * future second provider is a sibling of this file rather than a rewrite.
 *
 * Token refresh uses two mechanisms because one is not reliable. `tokenWillExpire`
 * is the intended path, but it does not fire in a throttled background tab --
 * and a CRM tab sits in the background most of the day. The interval is the
 * backstop, and a visibility check catches the case where both were starved.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Device } from "@twilio/voice-sdk";

// Refresh at 80% of TTL: comfortably before expiry, without thrashing.
const REFRESH_RATIO = 0.8;

export default function useTwilioDevice({
  enabled,
  fetchToken,
  onIncoming,
  onError,
  onRegistrationChange,
}) {
  const deviceRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const lastTokenAtRef = useRef(0);
  const ttlRef = useRef(3600);
  const destroyedRef = useRef(false);

  const [registered, setRegistered] = useState(false);
  const [deviceError, setDeviceError] = useState(null);

  const clearRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const scheduleRefresh = useCallback(
    (refreshFn) => {
      clearRefresh();
      const delay = Math.max(30, ttlRef.current * REFRESH_RATIO) * 1000;
      refreshTimerRef.current = setTimeout(refreshFn, delay);
    },
    [clearRefresh]
  );

  const refreshToken = useCallback(async () => {
    if (destroyedRef.current || !deviceRef.current) return;
    try {
      const { token, ttl_seconds: ttl } = await fetchToken();
      ttlRef.current = ttl || 3600;
      lastTokenAtRef.current = Date.now();
      deviceRef.current.updateToken(token);
      scheduleRefresh(refreshToken);
    } catch (err) {
      // Don't tear the Device down: an expired token breaks new calls but a
      // call already in progress keeps its media.
      setDeviceError({ code: "token_refresh_failed", message: "Could not refresh the phone connection." });
      if (onError) onError(err);
      scheduleRefresh(refreshToken);
    }
  }, [fetchToken, onError, scheduleRefresh]);

  /** Build and register the Device. Safe to call repeatedly. */
  const connect = useCallback(async () => {
    if (destroyedRef.current || deviceRef.current) return deviceRef.current;

    const { token, ttl_seconds: ttl } = await fetchToken();
    ttlRef.current = ttl || 3600;
    lastTokenAtRef.current = Date.now();

    const device = new Device(token, {
      codecPreferences: ["opus", "pcmu"],
      logLevel: "error",
      // Pearl owns the ringer (see telephony/ringer.js) so there is one
      // source of ringing rather than two competing ones.
      sounds: { incoming: "" },
    });

    device.on("registered", () => {
      setRegistered(true);
      setDeviceError(null);
      if (onRegistrationChange) onRegistrationChange("registered");
    });
    device.on("unregistered", () => {
      setRegistered(false);
      if (onRegistrationChange) onRegistrationChange("unregistered");
    });
    device.on("error", (err) => {
      setDeviceError({ code: err?.code, message: err?.message || "Phone connection error." });
      if (onError) onError(err);
    });
    device.on("incoming", (call) => {
      if (onIncoming) onIncoming(call);
    });
    device.on("tokenWillExpire", refreshToken);

    deviceRef.current = device;
    await device.register();
    scheduleRefresh(refreshToken);
    return device;
  }, [fetchToken, onIncoming, onError, onRegistrationChange, refreshToken, scheduleRefresh]);

  const disconnect = useCallback(() => {
    clearRefresh();
    const device = deviceRef.current;
    deviceRef.current = null;
    setRegistered(false);
    if (!device) return;
    try {
      device.disconnectAll();
      device.unregister();
      device.destroy();
    } catch {
      /* already gone */
    }
  }, [clearRefresh]);

  /** Place an outbound call. Params reach the TwiML App webhook. */
  const callOut = useCallback(async (params) => {
    const device = deviceRef.current;
    if (!device) throw new Error("The phone is not connected.");
    return device.connect({ params });
  }, []);

  const setInputDevice = useCallback(async (deviceId) => {
    const device = deviceRef.current;
    if (!device?.audio) return;
    try {
      await device.audio.setInputDevice(deviceId);
    } catch {
      /* device vanished (headset unplugged); browser falls back to default */
    }
  }, []);

  // A throttled tab can starve both tokenWillExpire and the interval, so
  // reconcile whenever the tab becomes visible again.
  useEffect(() => {
    if (!enabled) return undefined;
    const onVisible = () => {
      if (document.visibilityState !== "visible" || !deviceRef.current) return;
      const age = (Date.now() - lastTokenAtRef.current) / 1000;
      if (age > ttlRef.current * REFRESH_RATIO) refreshToken();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [enabled, refreshToken]);

  useEffect(() => {
    destroyedRef.current = false;
    return () => {
      destroyedRef.current = true;
      clearRefresh();
      const device = deviceRef.current;
      deviceRef.current = null;
      if (device) {
        try {
          device.destroy();
        } catch {
          /* ignore */
        }
      }
    };
  }, [clearRefresh]);

  return {
    device: deviceRef,
    registered,
    deviceError,
    connect,
    disconnect,
    callOut,
    setInputDevice,
    clearError: () => setDeviceError(null),
  };
}
